import React, { useEffect, useState } from 'react';
import {
  Button,
  Chip,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { InboxAssignee, InboxConversation } from 'data/api/conversation.api';
import { MemberTriages } from '../useMemberTriages';
import { ConversationSummary } from './ConversationSummary';
import { EventDetails } from './EventDetails';
import { TriagesSection } from './TriagesSection';

const UNASSIGNED = 'unassigned';

// `hidden` rather than unmounted while a triage takes over the rail, so a
// generated summary or loaded event details are still there on the way back.
const Section: React.FC<{
  title: string;
  icon?: React.ReactNode;
  hidden?: boolean;
}> = ({ title, icon, hidden, children }) => (
  <Stack sx={{ gap: '8px', display: hidden ? 'none' : 'flex' }}>
    <Stack direction="row" alignItems="center" gap={0.5}>
      {icon}
      <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#213B54' }}>
        {title}
      </Typography>
    </Stack>
    {children}
  </Stack>
);

interface IContextRailProps {
  conversation?: InboxConversation;
  assignees: InboxAssignee[];
  currentUserId?: string;
  busy: boolean;
  onAssign: (
    conversation: InboxConversation,
    assignedToUserId: string | null,
  ) => void;
  onOpenNotes: (conversation: InboxConversation) => void;
  onEscalate: (conversation: InboxConversation) => void;
  onDeescalate: (conversation: InboxConversation) => void;
  onRetryAssignees: () => void;
  canDeescalate: boolean;
  canSummarize: boolean;
  /** Read once per selected member by the page and shared with the thread. */
  triages: MemberTriages;
  openTriageId?: string;
  onSelectTriage: (triagesId?: string) => void;
}

export const ContextRail: React.FC<IContextRailProps> = ({
  conversation,
  assignees,
  currentUserId,
  busy,
  onAssign,
  onOpenNotes,
  onEscalate,
  onDeescalate,
  onRetryAssignees,
  canDeescalate,
  canSummarize,
  triages,
  openTriageId,
  onSelectTriage,
}) => {
  // Keyed by member, so switching families drops an open compose form on its
  // own: the form holds a subject and body written about one family, and it
  // must never reappear addressed to the next one's contacts.
  const [creatingFor, setCreatingFor] = useState<string>();
  // Sends still on the wire, keyed `create:<member>` / `reply:<triage>`. Held
  // here because this outlives the views that start them: a form or thread
  // reopened mid-send must not offer to send again into a duplicate triage or
  // an overlapping re-addressed reply.
  const [inFlight, setInFlight] = useState<string[]>([]);
  const onInFlight = (key: string, busy: boolean) =>
    setInFlight((current) =>
      busy ? [...current, key] : current.filter((entry) => entry !== key),
    );
  const memberUserId = conversation?.memberUserId;
  // Compose ownership never carries over to another family, even back to the
  // one that started it: that draft is gone with the form.
  useEffect(() => setCreatingFor(undefined), [memberUserId]);
  const creating = !!memberUserId && creatingFor === memberUserId;
  const takeover =
    creating ||
    triages.triages.some((triage) => triage.triagesId === openTriageId);

  return (
    <Stack
      sx={{
        width: '428px',
        minWidth: '360px',
        borderLeft: '1px solid #EFEDEB',
        backgroundColor: '#FBFAF9',
        height: '100%',
        // A triage being written or read scrolls inside itself, so its header
        // and send controls stay put.
        overflowY: takeover ? 'hidden' : 'auto',
        padding: takeover ? 0 : '16px',
        gap: '20px',
      }}
    >
      {!conversation ? (
        <Typography sx={{ fontSize: '14px', color: '#7D818C' }}>
          Select a conversation to see its context.
        </Typography>
      ) : (
        <>
          {/* The same flag the summary route authorizes on, so the section is
            never offered to someone the endpoint would refuse — and it goes
            away with the kill switch, without a frontend release. */}
          {canSummarize && (
            <Section
              hidden={takeover}
              title="Conversation summary"
              icon={
                <AutoAwesomeOutlinedIcon
                  sx={{ color: '#356766', fontSize: '18px' }}
                />
              }
            >
              {/* Its state is keyed by the member it was generated for, so a
                generation that lands after the selection moved on has no
                path to the screen. */}
              <ConversationSummary userId={conversation.memberUserId} />
            </Section>
          )}

          <Section title="Assignment" hidden={takeover}>
            <FormControl size="small" fullWidth>
              <Select
                value={conversation.assignedToUserId || UNASSIGNED}
                disabled={busy}
                // The staff list can fail to load independently of the inbox.
                // Without this the dropdown stays stuck at Unassigned/Me until
                // something unrelated re-runs the outer fetch.
                onOpen={onRetryAssignees}
                onChange={(event) =>
                  onAssign(
                    conversation,
                    event.target.value === UNASSIGNED
                      ? null
                      : String(event.target.value),
                  )
                }
                sx={{
                  height: '36px',
                  borderRadius: '10px',
                  color: '#213B54',
                  fontSize: '14px',
                }}
              >
                <MenuItem value={UNASSIGNED}>Unassigned</MenuItem>
                {currentUserId && <MenuItem value={currentUserId}>Me</MenuItem>}
                {assignees
                  .filter((assignee) => assignee.userId !== currentUserId)
                  .map((assignee) => (
                    <MenuItem key={assignee.userId} value={assignee.userId}>
                      {assignee.name}
                    </MenuItem>
                  ))}
                {/* Keeps the current assignee selectable — and visible — when
                  the staff list failed to load or no longer contains them. */}
                {!!conversation.assignedToUserId &&
                  conversation.assignedToUserId !== currentUserId &&
                  !assignees.some(
                    (assignee) =>
                      assignee.userId === conversation.assignedToUserId,
                  ) && (
                    <MenuItem value={conversation.assignedToUserId}>
                      {conversation.assignedToName || 'Current assignee'}
                    </MenuItem>
                  )}
              </Select>
            </FormControl>
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{ flexWrap: 'wrap' }}
            >
              {conversation.escalatedAt ? (
                <>
                  <Chip
                    label="Escalated"
                    size="small"
                    sx={{
                      backgroundColor: '#FEF3F2',
                      color: '#B42318',
                      fontWeight: 600,
                    }}
                  />
                  {canDeescalate && (
                    <Button
                      size="small"
                      disabled={busy}
                      onClick={() => onDeescalate(conversation)}
                      sx={{
                        color: '#7D818C',
                        textTransform: 'none',
                        fontWeight: 600,
                      }}
                    >
                      De-escalate
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  size="small"
                  disabled={busy}
                  onClick={() => onEscalate(conversation)}
                  sx={{
                    color: '#B42318',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Escalate
                </Button>
              )}
            </Stack>
            {conversation.splitAssignment && (
              <Typography sx={{ fontSize: '13px', color: '#8A5300' }}>
                Parts of this conversation are assigned to someone else.
                Reassigning puts all of it with one person.
              </Typography>
            )}
            {!!conversation.escalationReason && (
              <Typography sx={{ fontSize: '13px', color: '#7D818C' }}>
                {conversation.escalationReason}
              </Typography>
            )}
          </Section>

          {/* The one section here that holds a half-written message. Without
            the key, switching families under an open compose form keeps the
            subject and body typed about the previous one and would send them
            to this family's contacts. */}
          <TriagesSection
            key={conversation.memberUserId}
            userId={conversation.memberUserId}
            memberName={conversation.memberName}
            triages={triages}
            openTriageId={openTriageId}
            onSelectTriage={onSelectTriage}
            creating={creating}
            // Scoped to the member this section was rendered for: a send that
            // completes after the rep moved on must not close another
            // family's form.
            onCreatingChange={(next) => {
              const owner = conversation.memberUserId;
              setCreatingFor((current) => {
                if (next) return owner;
                return current === owner ? undefined : current;
              });
            }}
            inFlight={inFlight}
            onInFlight={onInFlight}
          />

          <Section title="Event details" hidden={takeover}>
            <EventDetails userId={conversation.memberUserId} />
          </Section>

          <Section title="Notes & resources" hidden={takeover}>
            <Button
              onClick={() => onOpenNotes(conversation)}
              endIcon={<OpenInNewRoundedIcon sx={{ fontSize: '16px' }} />}
              sx={{
                alignSelf: 'flex-start',
                color: '#356766',
                textTransform: 'none',
                fontWeight: 600,
                paddingX: 0,
              }}
            >
              Open this member&apos;s notes
            </Button>
          </Section>
        </>
      )}
    </Stack>
  );
};
